import { FC } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  IconButton,
  Tooltip
} from "@mui/material";
import { CommentDto } from "../../../api/Api";
import dayjs from "dayjs";
import {
  buildCommentDto,
  getCurrentCommentAuthor,
  renderCommentText
} from "../../../utils/commentUtils";
import { Send, Close } from "@mui/icons-material";
import { MentionInput } from "../mentionInput";
import { useIsEntityVp } from "../../../auth/entityVpContext";

type Props = {
  comments: CommentDto[];
  currentComment: string;
  onCurrentCommentChange: (value: string) => void;
  loading?: boolean;
  saveComment?: (comment: CommentDto) => void;
  onDeleteComment?: (timestamp: string) => void;
};

export const CommentsSection: FC<Props> = ({
  comments,
  currentComment,
  loading,
  onCurrentCommentChange,
  saveComment,
  onDeleteComment
}) => {
  const currentUser = getCurrentCommentAuthor();
  // A VP reads every lifecycle but writes to none of them, comments included.
  const isEntityVp = useIsEntityVp();
  return (
    <>
      {!isEntityVp && (
        <Box display="inline-flex" gap={2} sx={{ alignItems: "flex-end" }}>
          <Box sx={{ flex: 1 }}>
            <MentionInput
              value={currentComment}
              onChange={onCurrentCommentChange}
              label="Comment"
              multiline
              rows={2}
              maxRows={2}
            />
          </Box>
          {saveComment ? (
            <Box display="inline-flex" alignItems="center">
              <Button
                variant="contained"
                onClick={() => saveComment(buildCommentDto(currentComment))}
                disabled={!currentComment.trim()}
                loading={loading}
                endIcon={<Send />}
              >
                Add
              </Button>
            </Box>
          ) : null}
        </Box>
      )}

      {comments.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Previous Comments
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: 280,
              overflowY: "auto",
              paddingRight: 1,
              backgroundColor: "background.default"
            }}
          >
            {comments
              ?.map((x) => x)
              .reverse()
              .map((comment, index) => {
                const formattedTimestamp = dayjs(comment.timestamp).format(
                  "DD/MM/YY HH:mm"
                );
                return (
                  <Box
                    key={`${comment.authorName || comment.author || "unknown"}-${index}`}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        padding: 1
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1,
                          flexWrap: "wrap"
                        }}
                      >
                        <Typography variant="body2">
                          {comment.authorName || comment.author || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formattedTimestamp}:
                        </Typography>
                        {!isEntityVp &&
                          onDeleteComment &&
                          comment.author === currentUser &&
                          comment.timestamp && (
                            <Tooltip title="Delete comment">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  onDeleteComment(comment.timestamp!)
                                }
                                sx={{ marginLeft: "auto", padding: 0.25 }}
                              >
                                <Close fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {renderCommentText(
                          comment.comment ?? "",
                          (display, _email, key) => (
                            <Box
                              component="span"
                              key={key}
                              sx={{
                                color: "primary.main",
                                backgroundColor: "action.selected",
                                borderRadius: "3px",
                                padding: "0 3px"
                              }}
                            >
                              @{display}
                            </Box>
                          )
                        )}
                      </Typography>
                    </Box>
                    {index < comments?.length - 1 && <Divider />}
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}
    </>
  );
};
